import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Select, Spin } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import VideoPlayer, { Shape, DrawingTool } from '../VideoPlayer/VideoPlayer';
import PdfPlayer from '../PdfPlayer';
import styles from '../../pages/projects/Projects.module.scss';
import vpStyles from '../VideoPlayer/VideoPlayer.module.scss';
import VersionSelectOptionLabel from '../VersionSelectOptionLabel/VersionSelectOptionLabel';
import { formatVersionSelectorLabel } from '../../utils/versionDisplayName';
import {
  filterSiblingFiles,
  getSiblingFileNavigationIndexByPath,
  isFileEntry,
  normalizeSiblingNavPath,
  sortSiblingFilesForNavigation,
} from '../../utils/siblingFileNavigation';
import { shouldIgnoreVersionNavKeyboard } from '../../utils/versionNavKeyboard';
import VersionPlayerNav from './VersionPlayerNav';
import sectionStyles from './VideoPlayerSection.module.scss';

interface VideoPlayerSectionProps {
  fileDetail: any;
  versions: any[];
  versionsLoading: boolean;
  fps?: number;
  selectedCommentId?: number | null;
  
  // Main player states
  selectedVersion: number;
  currentVideoUrl: string;
  videoLoading: boolean;
  shapes: Shape[];
  drawingTool: DrawingTool;
  drawingColor: string;
  selectedShapeId: string | null;
  currentVideoTime: number;
  isPaused: boolean;
  selectedRange: [number, number] | null;
  externalRange: [number, number] | null;
  currentFrameIndex?: number;
  
  // Compare player states
  compareMode: boolean;
  activePlayer: number;
  selectedVersionCompare?: number | null;
  currentVideoUrlCompare: string;
  shapesCompare: Shape[];
  selectedShapeIdCompare: string | null;
  currentVideoTimeCompare: number;
  isPausedCompare: boolean;
  selectedRangeCompare: [number, number] | null;
  /** Compare player: yorum seçiminde alt range slider'ı gizlemek için (FileDetail) */
  externalRangeCompare?: [number, number] | null;
  currentFrameIndexCompare?: number;
  
  // Drawing states
  isDrawingMode: boolean;
  /** Alt kontrol çubuğunda (expand solunda) çizim araçları */
  drawingToolbox?: React.ReactNode;
  
  // Handlers for main player
  onVersionChange: (version: string) => void;
  onShapesChange: (shapes: Shape[]) => void;
  onTextToolClick: (pos: { x: number, y: number }) => void;
  onShapeSelection: (shapeId: string | null) => void;
  onDrawingComplete: () => void;
  onCurrentTimeChange: (time: number, fps: number) => void;
  onPlay: () => void;
  onSeek: () => void;
  onDurationChange: (duration: number) => void;
  onRangeSelect: (range: [number, number]) => void;
  /** Clear range selection when user presses ESC (wired from VideoPlayer) */
  onRangeSelectCancel?: () => void;
  /** Aralık üstü play ile çizim araçlarını kapat (FileDetail) */
  onExitDrawingToolbox?: () => void;

  // Handlers for compare player
  onCompareVersionChange: (version: string) => void;
  onShapesCompareChange: (shapes: Shape[]) => void;
  onTextToolClickCompare: (pos: { x: number, y: number }) => void;
  onShapeSelectionCompare: (shapeId: string | null) => void;
  onDrawingCompleteCompare: () => void;
  onCurrentTimeChangeCompare: (time: number, fps: number) => void;
  onPlayCompare: () => void;
  onSeekCompare: () => void;
  onDurationChangeCompare: (duration: number) => void;
  onRangeSelectCompare: (range: [number, number]) => void;
  
  // Compare mode handlers
  onActivePlayerChange: (player: number) => void;
  
  // Comment handlers
  onCommentPinClick: (commentId: number) => void;
  getFormattedComments: () => any[];
  
  // Show resolved pins
  showResolved?: boolean;
  /** Üst çubukta versiyon/compare kontrolleri varken oyuncu kartındaki seçicileri gizle */
  suppressInlineVersionChrome?: boolean;
  /** Compare header'daki "Add Version" butonu tetikleyicisi. panel: hangi panel için */
  onAddVersion?: (panel: 1 | 2) => void;

  /** PDF: active page (comment = frame) */
  onPdfPageChange?: (page: number) => void;
  /** PDF: user changed page (comment list can switch to this-page-only mode) */
  onPdfUserNavigate?: () => void;
  pdfPageJumpRequest?: { token: number; page: number } | null;
  onPdfDocumentInfo?: (info: { numPages: number }) => void;

  /** Landscape + compare: yorum çekmecesi açıkken tek video paneli */
  compareDualSinglePanel?: boolean;
  /** Landscape + compare: kontrol çubuğunda yorum ikonu */
  compareCommentToggleSlot?: React.ReactNode;

  /** Compare panel başlığı X: 1 = sol, 2 = sağ */
  onComparePanelClose?: (panel: 1 | 2) => void;

  /** Aynı klasördeki kardeş dosyalar (ok navigasyonu) */
  siblingFiles?: any[];
  siblingFilesLoading?: boolean;
  fileSwitchLoading?: boolean;
  onNavigateToSibling?: (file: any) => void;
  /** Mobilde player üstündeki overlay okları gizle (nav meta satırına taşınır). */
  hideSiblingOverlayNav?: boolean;
  /** PDF sayfa sayısı: çok sayfalıysa yön tuşları sayfa değiştirir (sibling değil). */
  pdfNumPages?: number | null;
}

const VideoPlayerSection: React.FC<VideoPlayerSectionProps> = ({
  fileDetail,
  versions,
  versionsLoading,
  fps = 30,
  selectedCommentId,
  selectedVersion,
  currentVideoUrl,
  videoLoading,
  shapes,
  drawingTool,
  drawingColor,
  selectedShapeId,
  currentVideoTime,
  isPaused,
  selectedRange,
  externalRange,
  currentFrameIndex,
  compareMode,
  activePlayer,
  selectedVersionCompare,
  currentVideoUrlCompare,
  shapesCompare,
  selectedShapeIdCompare,
  currentVideoTimeCompare,
  isPausedCompare,
  selectedRangeCompare,
  externalRangeCompare = null,
  currentFrameIndexCompare,
  isDrawingMode,
  drawingToolbox,
  onVersionChange,
  onShapesChange,
  onTextToolClick,
  onShapeSelection,
  onDrawingComplete,
  onCurrentTimeChange,
  onPlay,
  onSeek,
  onDurationChange,
  onRangeSelect,
  onRangeSelectCancel,
  onExitDrawingToolbox,
  onCompareVersionChange,
  onShapesCompareChange,
  onTextToolClickCompare,
  onShapeSelectionCompare,
  onDrawingCompleteCompare,
  onCurrentTimeChangeCompare,
  onPlayCompare,
  onSeekCompare,
  onDurationChangeCompare,
  onRangeSelectCompare,
  onActivePlayerChange,
  onCommentPinClick,
  getFormattedComments,
  showResolved,
  suppressInlineVersionChrome = false,
  onAddVersion,
  onPdfPageChange,
  onPdfUserNavigate,
  pdfPageJumpRequest,
  onPdfDocumentInfo,
  compareDualSinglePanel = false,
  compareCommentToggleSlot,
  onComparePanelClose,
  siblingFiles = [],
  siblingFilesLoading = false,
  fileSwitchLoading = false,
  onNavigateToSibling,
  hideSiblingOverlayNav = false,
  pdfNumPages = null,
}) => {
  const location = useLocation();
  // Compare mode değiştiğinde canvas boyutlarını güncelle
  useEffect(() => {
    if (compareMode) {
      // Compare mode'a geçişte canvas boyutlarını güncellemek için kısa bir gecikme
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [compareMode]);

  const [showResolvedPins, setShowResolvedPins] = useState<boolean>(false);
  useEffect(() => {
    const handleCustom = (e: any) => {
      if (typeof e?.detail === 'boolean') {
        setShowResolvedPins(e.detail);
      }
    };
    window.addEventListener('showResolvedChanged' as any, handleCustom as any);
    return () => {
      window.removeEventListener('showResolvedChanged' as any, handleCustom as any);
    };
  }, []);

  const effectiveShowResolved = typeof showResolved === 'boolean' ? showResolved : showResolvedPins;

  const mainControlBarToolbox =
    drawingToolbox && (!compareMode || activePlayer === 1)
      ? drawingToolbox
      : undefined;
  const compareControlBarToolbox =
    drawingToolbox && compareMode && activePlayer === 2
      ? drawingToolbox
      : undefined;

  // Get file type helper (fileName ve videoUrl'den uzantı; URL path/query'de mp4 vb. varsa video say)
  const getFileType = (fileName: string, videoUrl?: string) => {
    let extension: string | undefined = undefined;

    if (fileName && typeof fileName === 'string' && fileName.includes('.')) {
      const parts = fileName.split('.');
      if (parts.length > 1) {
        extension = parts.pop()?.toLowerCase();
      }
    }

    if ((!extension || extension === fileName?.toLowerCase()) && videoUrl) {
      const cleanUrl = videoUrl.split('?')[0].split('#')[0];
      const urlExtension = cleanUrl.split('.').pop()?.toLowerCase().trim();
      if (urlExtension && urlExtension.length <= 5) {
        const cleanExtension = urlExtension.replace(/[^a-z0-9]/gi, '');
        if (cleanExtension) {
          extension = cleanExtension;
        }
      }
    }

    if (!extension && videoUrl) {
      const lower = videoUrl.toLowerCase();
      if (lower.includes('.mp4') || lower.includes('mp4')) extension = 'mp4';
      else if (lower.includes('.webm') || lower.includes('webm')) extension = 'webm';
      else if (lower.includes('.mov')) extension = 'mov';
      else if (lower.includes('.avi')) extension = 'avi';
    }

    let result = 'other';
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension || '')) {
      result = 'video';
    } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'ico'].includes(extension || '')) {
      result = 'image';
    } else if (['pdf'].includes(extension || '')) {
      result = 'pdf';
    } else if (['doc', 'docx'].includes(extension || '')) {
      result = 'document';
    }

    return result;
  };

  const primaryFileType = useMemo(() => {
    if (!fileDetail) return 'other';
    const fileUrl =
      currentVideoUrl ||
      versions?.[0]?.fileUrl ||
      fileDetail.url ||
      fileDetail.fileUrl ||
      fileDetail.downloadUrl ||
      fileDetail.path ||
      '';
    const selectedVersionData = versions.find((v: any) => v.id === selectedVersion);
    const fileNameForType =
      selectedVersionData?.name ||
      (selectedVersionData?.path ? selectedVersionData.path.split('/').pop() : undefined) ||
      fileDetail.name ||
      '';
    return getFileType(fileNameForType, fileUrl);
  }, [fileDetail, versions, selectedVersion, currentVideoUrl]);

  const versionOptionClasses = useMemo(
    () => ({
      row: styles.versionSelectOptionRow,
      name: styles.versionSelectOptionName,
      date: styles.versionSelectOptionDate,
    }),
    []
  );

  const mainVersionOptions = useMemo(() => {
    if (!versions?.length) return [];
    return versions
      .filter((v: any) => v.id !== selectedVersionCompare)
      .map((version: any) => ({
        label: formatVersionSelectorLabel(version.name),
        value: version.name,
        disabled: false,
      }));
  }, [versions, selectedVersionCompare]);

  const compareVersionOptions = useMemo(() => {
    if (!versions?.length) return [];
    return versions
      .filter((v: any) => v.id !== selectedVersion)
      .map((version: any) => ({
        label: formatVersionSelectorLabel(version.name),
        value: version.name,
        disabled: false,
      }));
  }, [versions, selectedVersion]);

  const versionOptionRender = useCallback(
    (opt: any) => {
      const value = (opt.data?.value ?? opt.value) as string;
      const v = versions.find((x: any) => x.name === value);
      if (!v) return <span>{opt.data?.label ?? opt.label}</span>;
      return <VersionSelectOptionLabel version={v} classes={versionOptionClasses} />;
    },
    [versions, versionOptionClasses]
  );

  const handleVersionSelect = useCallback((value: string, isCompare = false) => {
    const selectedVersionData = versions.find((v: any) => v.name === value);
    if (isCompare) {
      onCompareVersionChange(value);
    } else {
      onVersionChange(value);
      if (selectedVersionData?.name) {
        window.location.hash = selectedVersionData.name;
      }
    }
  }, [versions, onVersionChange, onCompareVersionChange]);

  const navigableSiblingFiles = useMemo(
    () => sortSiblingFilesForNavigation(filterSiblingFiles(siblingFiles)),
    [siblingFiles]
  );

  // URL = tek doğruluk kaynağı: /int/<uuid> hangi sibling'e denk geliyorsa o index.
  const currentNavPath = normalizeSiblingNavPath(location.pathname);

  const currentSiblingIndex = useMemo(
    () => getSiblingFileNavigationIndexByPath(navigableSiblingFiles, currentNavPath),
    [navigableSiblingFiles, currentNavPath]
  );


  const navigateSiblingFile = useCallback(
    (direction: 'prev' | 'next') => {
      if (navigableSiblingFiles.length <= 1) return;

      const navPath = normalizeSiblingNavPath(window.location.pathname);
      const fromIndex = getSiblingFileNavigationIndexByPath(navigableSiblingFiles, navPath);
      if (fromIndex < 0) return;

      const nextIndex = direction === 'prev' ? fromIndex - 1 : fromIndex + 1;
      const target = navigableSiblingFiles[nextIndex];
      if (!target || !isFileEntry(target) || !onNavigateToSibling) return;
      onNavigateToSibling(target);
    },
    [navigableSiblingFiles, onNavigateToSibling]
  );

  const canGoPrevSibling = currentSiblingIndex > 0;
  const canGoNextSibling =
    currentSiblingIndex >= 0 && currentSiblingIndex < navigableSiblingFiles.length - 1;

  const navigateSiblingRef = useRef(navigateSiblingFile);
  const canGoPrevSiblingRef = useRef(canGoPrevSibling);
  const canGoNextSiblingRef = useRef(canGoNextSibling);
  const siblingNavBusyRef = useRef(false);
  navigateSiblingRef.current = navigateSiblingFile;
  canGoPrevSiblingRef.current = canGoPrevSibling;
  canGoNextSiblingRef.current = canGoNextSibling;
  siblingNavBusyRef.current = siblingFilesLoading || fileSwitchLoading;

  const playerContentBusy =
    fileSwitchLoading || versionsLoading || videoLoading;

  // Sibling nav (yön tuşları): yalnızca tek frameli dosyalarda (görsel/diğer + tek sayfa PDF).
  // Video'da 5sn seek (VideoPlayer), çok sayfalı PDF'te sayfa değişimi (PdfPlayer) yapılır.
  const pdfIsMultiPage = primaryFileType === 'pdf' && (pdfNumPages ?? 0) > 1;
  useEffect(() => {
    if (
      navigableSiblingFiles.length <= 1 ||
      primaryFileType === 'video' ||
      pdfIsMultiPage
    ) {
      return;
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (shouldIgnoreVersionNavKeyboard(e)) return;
      if (e.repeat) return;
      if (siblingNavBusyRef.current) return;

      if (e.key === 'ArrowLeft' && canGoPrevSiblingRef.current) {
        e.preventDefault();
        e.stopImmediatePropagation();
        navigateSiblingRef.current('prev');
      } else if (e.key === 'ArrowRight' && canGoNextSiblingRef.current) {
        e.preventDefault();
        e.stopImmediatePropagation();
        navigateSiblingRef.current('next');
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [navigableSiblingFiles.length, primaryFileType, pdfIsMultiPage]);

  const getSelectedVersionName = useCallback((versionId: number | null | undefined) => {
    if (!versionId || !versions.length) return undefined;
    const v = versions.find((x: any) => x.id === versionId);
    return v?.name || undefined;
  }, [versions]);

  const versionDropdownRender = useCallback((panel: 1 | 2) => {
    if (!onAddVersion) return undefined;
    return (menu: React.ReactElement) => (
      <div>
        {menu}
        <div
          className={styles.versionSelectDropdownFooter}
          onMouseDown={(e) => e.preventDefault()}
        >
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            className={styles.versionSelectAddVersionBtn}
            onClick={() => onAddVersion(panel)}
          >
            + Add Version
          </Button>
        </div>
      </div>
    );
  }, [onAddVersion]);

  const buildDualHeaders = () => {
    if (!compareMode) return undefined;

    const mainVersionName = getSelectedVersionName(selectedVersion);
    const compareVersionName = getSelectedVersionName(selectedVersionCompare ?? null);

    const mainHeader = (
      <>
        <div
          className={`${vpStyles.panelHeaderCheckbox} ${activePlayer === 1 ? vpStyles.panelHeaderCheckboxActive : ''}`}
          onClick={(e) => { e.stopPropagation(); onActivePlayerChange(1); }}
        >
          {activePlayer === 1 && <div className={vpStyles.panelHeaderCheckboxDot} />}
        </div>
        <span className={vpStyles.panelHeaderName} title={fileDetail?.name}>
          {fileDetail?.name}
        </span>
        <div className={vpStyles.panelHeaderActions}>
          <Select
            className={styles.versionSelect}
            popupClassName={styles.playerPageTopBarSelectDropdown}
            popupMatchSelectWidth={false}
            value={mainVersionName}
            onChange={(val) => handleVersionSelect(val, false)}
            loading={versionsLoading}
            options={mainVersionOptions}
            optionRender={versionOptionRender}
            onClick={(e) => e.stopPropagation()}
            dropdownRender={versionDropdownRender(1)}
          />
        </div>
      </>
    );

    const compareHeader = (
      <>
        <div
          className={`${vpStyles.panelHeaderCheckbox} ${activePlayer === 2 ? vpStyles.panelHeaderCheckboxActive : ''}`}
          onClick={(e) => { e.stopPropagation(); onActivePlayerChange(2); }}
        >
          {activePlayer === 2 && <div className={vpStyles.panelHeaderCheckboxDot} />}
        </div>
        <span className={vpStyles.panelHeaderName} title={fileDetail?.name}>
          {fileDetail?.name}
        </span>
        <div className={vpStyles.panelHeaderActions}>
          <Select
            className={styles.versionSelect}
            popupClassName={styles.playerPageTopBarSelectDropdown}
            popupMatchSelectWidth={false}
            value={compareVersionName}
            onChange={(val) => handleVersionSelect(val, true)}
            loading={versionsLoading}
            options={compareVersionOptions}
            optionRender={versionOptionRender}
            onClick={(e) => e.stopPropagation()}
            dropdownRender={versionDropdownRender(2)}
          />
        </div>
      </>
    );

    return { main: mainHeader, compare: compareHeader };
  };

  const buildCompareVideoConfig = () => {
    if (!compareMode || !selectedVersionCompare || !currentVideoUrlCompare) return undefined;
    const compareVersionData = fileDetail?.versions?.find((v: any) => v.id === selectedVersionCompare);
    const compareFileNameForType = compareVersionData?.name
      || (compareVersionData?.path ? compareVersionData.path.split('/').pop() : undefined)
      || fileDetail?.name || '';
    const compareFileType = getFileType(compareFileNameForType, currentVideoUrlCompare);
    if (compareFileType !== 'video') return undefined;
    return {
      src: currentVideoUrlCompare,
      poster: fileDetail.thumbnailUrl,
      shapes: shapesCompare,
      onShapesChange: onShapesCompareChange,
      onTextToolClick: onTextToolClickCompare,
      selectedShapeId: selectedShapeIdCompare,
      onShapeSelection: onShapeSelectionCompare,
      onDrawingComplete: onDrawingCompleteCompare,
      isDrawingEnabled: isDrawingMode && activePlayer === 2 && selectedCommentId == null,
      onCurrentTimeChange: onCurrentTimeChangeCompare,
      onPlay: onPlayCompare,
      onSeek: onSeekCompare,
      onDurationChange: onDurationChangeCompare,
      onRangeSelect: onRangeSelectCompare,
      selectedRange: selectedRangeCompare,
      externalRange: externalRangeCompare,
      controlBarToolbox: compareControlBarToolbox,
      comments: getFormattedComments(),
    };
  };

  const renderFileViewer = () => {
    if (!fileDetail) return null;

    const fileUrl = currentVideoUrl ||
                   versions?.[0]?.fileUrl ||
                   fileDetail.url ||
                   fileDetail.fileUrl ||
                   fileDetail.downloadUrl ||
                   fileDetail.path || '';

    const selectedVersionData = versions.find((v: any) => v.id === selectedVersion);
    const fileNameForType = selectedVersionData?.name
      || (selectedVersionData?.path ? selectedVersionData.path.split('/').pop() : undefined)
      || fileDetail.name || '';

    const fileType = getFileType(fileNameForType, fileUrl);

    if (fileType === 'video') {
      if (videoLoading && !playerContentBusy) {
        return (
          <div className={styles.loadingContainer}>
            <h3>{fileDetail.name}</h3>
            <p>Video yükleniyor...</p>
          </div>
        );
      }
      if (!fileUrl) {
        return (
          <div className={styles.errorContainer}>
            <h3>{fileDetail.name}</h3>
            <p>Video URL'i bulunamadı</p>
          </div>
        );
      }

      const compareVideoConfig = buildCompareVideoConfig();
      const dualHeaders = buildDualHeaders();

      return (
        <VideoPlayer
          key={`${fileDetail.id}-${selectedVersion}${compareVideoConfig ? `-cmp-${selectedVersionCompare}` : ''}`}
          src={fileUrl}
          fileType={fileType}
          poster={fileDetail.thumbnailUrl}
          isDrawingEnabled={isDrawingMode && (!compareMode || activePlayer === 1) && selectedCommentId == null}
          title={fileDetail.name}
          shapes={shapes}
          tool={drawingTool}
          color={drawingColor}
          onShapesChange={onShapesChange}
          onTextToolClick={onTextToolClick}
          selectedShapeId={selectedShapeId}
          onShapeSelection={onShapeSelection}
          onDrawingComplete={onDrawingComplete}
          onCurrentTimeChange={onCurrentTimeChange}
          currentVideoTime={currentVideoTime}
          isPaused={isPaused}
          onPlay={onPlay}
          onSeek={onSeek}
          onDurationChange={onDurationChange}
          selectedRange={selectedRange}
          onRangeSelect={onRangeSelect}
          onRangeSelectCancel={onRangeSelectCancel}
          onExitDrawingToolbox={onExitDrawingToolbox}
          externalRange={externalRange}
          currentFrameIndex={currentFrameIndex}
          comments={getFormattedComments()}
          onCommentClick={onCommentPinClick}
          selectedCommentId={selectedCommentId}
          fps={fps}
          compareMode={compareMode}
          showResolved={effectiveShowResolved}
          controlBarToolbox={mainControlBarToolbox}
          compareVideo={compareVideoConfig}
          activeVideoPanel={activePlayer as 1 | 2}
          onActiveVideoPanelChange={(panel) => onActivePlayerChange(panel)}
          dualHeaders={dualHeaders}
          onComparePanelClose={compareMode ? onComparePanelClose : undefined}
          dualCompareShowSinglePanel={compareDualSinglePanel}
          compareCommentToggleSlot={compareCommentToggleSlot}
        />
      );
    } else if (fileType === 'image') {
      if (!fileUrl) {
        return (
          <div className={styles.errorContainer}>
            <h3>{fileDetail.name}</h3>
            <p>Image URL'i bulunamadı</p>
          </div>
        );
      }
      return (
        <div className={styles.imageContainer}>
          <VideoPlayer
            key={`${fileDetail.id}-${selectedVersion}`}
            src={fileUrl}
            fileType={fileType}
            poster={fileDetail.thumbnailUrl}
            isDrawingEnabled={isDrawingMode && selectedCommentId == null}
            title={fileDetail.name}
            shapes={shapes}
            tool={drawingTool}
            color={drawingColor}
            onShapesChange={onShapesChange}
            onTextToolClick={onTextToolClick}
            selectedShapeId={selectedShapeId}
            onShapeSelection={onShapeSelection}
            onDrawingComplete={onDrawingComplete}
            onCurrentTimeChange={onCurrentTimeChange}
            currentVideoTime={currentVideoTime}
            isPaused={isPaused}
            onPlay={onPlay}
            onSeek={onSeek}
            onDurationChange={onDurationChange}
            selectedRange={selectedRange}
            onRangeSelect={onRangeSelect}
            onRangeSelectCancel={onRangeSelectCancel}
            onExitDrawingToolbox={onExitDrawingToolbox}
            externalRange={externalRange}
            currentFrameIndex={currentFrameIndex}
            comments={getFormattedComments()}
            onCommentClick={onCommentPinClick}
            selectedCommentId={selectedCommentId}
            fps={fps}
            compareMode={compareMode}
            showResolved={effectiveShowResolved}
            controlBarToolbox={mainControlBarToolbox}
          />
        </div>
      );
    } else if (fileType === 'pdf') {
      if (!fileUrl) {
        return (
          <div className={styles.errorContainer}>
            <h3>{fileDetail.name}</h3>
            <p>PDF URL not found</p>
          </div>
        );
      }
      return (
        <div className={styles.pdfPlayerWrap}>
          <PdfPlayer
            fileUrl={fileUrl}
            title={fileDetail.name}
            drawingToolbox={mainControlBarToolbox}
            isDrawingMode={isDrawingMode}
            selectedCommentId={selectedCommentId ?? null}
            shapes={shapes}
            drawingTool={drawingTool}
            drawingColor={drawingColor}
            selectedShapeId={selectedShapeId}
            onShapesChange={onShapesChange}
            onTextToolClick={onTextToolClick}
            onShapeSelection={onShapeSelection}
            onDrawingComplete={onDrawingComplete}
            onPageChange={onPdfPageChange}
            onPdfUserNavigate={onPdfUserNavigate}
            pageJumpRequest={pdfPageJumpRequest ?? null}
            onDocumentInfo={onPdfDocumentInfo}
            pdfComments={getFormattedComments()}
            pdfCommentVersionId={selectedVersion}
            showResolvedPins={effectiveShowResolved}
          />
        </div>
      );
    } else {
      return (
        <div className={styles.otherFileContainer}>
          <h3>{fileDetail.name}</h3>
          <p>File Type: {fileType}</p>
          {fileDetail.thumbnailUrl && (
            <img
              src={fileDetail.thumbnailUrl}
              alt={fileDetail.name}
              className={styles.otherFileThumbnail}
            />
          )}
        </div>
      );
    }
  };

  return (
    <div className={styles.fileViewer}>
      <div className={`${styles.videoContainer} ${styles.singleMode}`}>
        <div
          className={`${styles.playerWrapper}${
            compareMode ? ` ${sectionStyles.playerWrapperCompareMode}` : ''
          }`}
        >
          <div
            className={`${styles.videoPlayerContainer} ${sectionStyles.playerSwitchHost} ${
              compareMode ? styles.compareMode : ''
            }${playerContentBusy ? ` ${sectionStyles.playerContentBusy}` : ''}`}
          >
            {renderFileViewer()}
            {playerContentBusy ? (
              <div className={sectionStyles.playerSwitchOverlay} aria-hidden="true">
                <Spin size="large" />
              </div>
            ) : null}
          </div>
          {hideSiblingOverlayNav ? null : (
            <VersionPlayerNav
              total={navigableSiblingFiles.length}
              currentIndex={currentSiblingIndex}
              canGoPrev={canGoPrevSibling}
              canGoNext={canGoNextSibling}
              onPrev={() => navigateSiblingFile('prev')}
              onNext={() => navigateSiblingFile('next')}
              disabled={siblingFilesLoading || fileSwitchLoading}
              showKeyboardHint={primaryFileType !== 'video' && !pdfIsMultiPage}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoPlayerSection; 